import { z } from 'zod';
import { DEFAULT_PORTS } from '@/core/engines';
import { azureStorageEndpointsSchema, storageAttachmentScopeSchema, storageServiceSchema } from '@/types/azureStorageSchema';

/**
 * Database engine types supported by the connection form.
 */
export type DatabaseEngine = 'mssql' | 'pgsql' | 'sqlite' | 'mysql' | 'oracle' | 'kusto' | 'azurestorage';

/**
 * Authentication types per engine
 */
export type MssqlAuthType =
  | 'SQL Server authentication'
  | 'Windows authentication'
  | 'Microsoft Entra ID (Device Code)'
  | 'Microsoft Entra ID (Interactive Browser)';
export type PgsqlAuthType = 'Password' | 'No password';
export type MysqlAuthType = 'Password' | 'No password';
export type OracleAuthType = 'Password';
export type OracleConnectionStringType = 'ezconnect' | 'tns_descriptor' | 'tns_alias';
export type OracleDebugProtocol = 'dbms_debug' | 'jdwp';
/** Kusto (Azure Data Explorer) auth options. */
export type KustoAuthType = 'Microsoft Entra ID (Interactive Browser)' | 'Service Principal' | 'Azure CLI';
/** Azure Storage auth options (spec §4.2): three storage literals + the Entra literals shared with Kusto. */
export type AzureStorageAuthType = 'Account Key' | 'Shared Access Signature' | 'Anonymous' | KustoAuthType;
export type AuthenticationType = MssqlAuthType | PgsqlAuthType | MysqlAuthType | OracleAuthType | KustoAuthType | AzureStorageAuthType;

// Re-export DEFAULT_PORTS for backward compatibility
export { DEFAULT_PORTS };

/** Azure Storage's "Connect using" modes (the type is `StorageConnectMode` in `azureStorageFormRules.ts`). */
const storageConnectModeSchema = z.union([z.literal('account'), z.literal('emulator'), z.literal('resource')]);

export const connectionFormSchema = z.object({
  engine: z.union([z.literal('mssql'), z.literal('pgsql'), z.literal('sqlite'), z.literal('mysql'), z.literal('oracle'), z.literal('kusto'), z.literal('azurestorage')]).default('mssql'),
  alias: z.string().optional(),
  server: z.string().min(1, 'Server host is required'),
  port: z.union([
    z.literal('<auto>'),
    z.string().min(1, 'Port is required'),
    z.number().positive('Port must be a positive number'),
  ]),
  authentication: z.union([
    // MSSQL auth types
    z.literal('SQL Server authentication'),
    z.literal('Windows authentication'),
    z.literal('Microsoft Entra ID (Device Code)'),
    z.literal('Microsoft Entra ID (Interactive Browser)'),
    // PostgreSQL auth types
    z.literal('Password'),
    z.literal('No password'),
    // Kusto auth types (Interactive Browser literal reused from MSSQL above)
    z.literal('Service Principal'),
    z.literal('Azure CLI'),
    // Azure Storage auth types (Entra literals reused from above)
    z.literal('Account Key'),
    z.literal('Shared Access Signature'),
    z.literal('Anonymous'),
  ]),
  login: z.string().optional(),
  password: z.string().optional(),
  database: z.string().optional(),
  limitToDatabase: z.boolean().optional(),
  // MSSQL-specific options
  entraTenantId: z.string().optional(),
  encrypt: z.boolean().optional(),
  trustServerCertificate: z.boolean().optional(),
  // PostgreSQL-specific options
  ssl: z.boolean().optional(),
  // SQLite-specific options
  readOnly: z.boolean().optional(),
  // Oracle-specific options
  oracleServiceName: z.string().optional(),
  oracleUseSID: z.boolean().optional(),
  oracleInitialSchema: z.string().optional(),
  oracleConnectionStringType: z.union([z.literal('ezconnect'), z.literal('tns_descriptor'), z.literal('tns_alias')]).optional(),
  oracleTnsDescriptor: z.string().optional(),
  oracleTnsAlias: z.string().optional(),
  /**
   * Oracle TNS modes: the server was synthesized from the TNS target, not typed (see `Connection` and `connectionRecord`).
   * Loaded with an edited record and REVOKED by any user edit of Server (C1 review C1-02): equality never means ownership.
   */
  oracleServerFromTnsTarget: z.boolean().optional(),
  oracleTnsAdmin: z.string().optional(),
  oracleWalletLocation: z.string().optional(),
  oracleDebugProtocol: z.union([z.literal('dbms_debug'), z.literal('jdwp')]).optional(),
  // Azure Storage-specific options (the four dial fields + the portal-link ARM id)
  azureStorageEndpoints: azureStorageEndpointsSchema.optional(),
  azureStorageServices: z.array(storageServiceSchema).optional(),
  azureStorageScope: storageAttachmentScopeSchema.optional(),
  azureStorageEmulator: z.boolean().optional(),
  azureResourceId: z.string().optional(),
  azureResourceIdOrigin: z.enum(['picker', 'user']).optional(),
  azureStorageDataAccess: z.union([z.literal('entra'), z.literal('account-key-via-arm')]).optional(),
  // S7 (plan §3.2): the Entra named shares. Transient (never saved): the File / Folder choice of an ambiguous share path.
  azureStorageNamedShares: z.array(z.string()).optional(),
  azureStorageShareChoice: z.union([z.literal('unset'), z.literal('file'), z.literal('directory')]).optional(),
  // Transient (never saved, never tested; C3): the user's own "Connect using" pick, held while the values still read what
  // they read right after it (`storageModeOnScreen`). A form value, so an engine round trip keeps it in the draft.
  azureStorageModePick: z
    .object({ mode: storageConnectModeSchema, derivedAtPick: storageConnectModeSchema })
    .optional(),
  // SSH tunnel block (network engines only — UI hides for SQLite)
  sshTunnel: z
    .object({
      enabled: z.boolean(),
      host: z.string(),
      port: z.number().int().positive().default(22),
      user: z.string(),
      auth: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('agent') }),
        z.object({
          kind: z.literal('key'),
          keyPath: z.string(),
          hasStoredPassphrase: z.boolean().default(false),
        }),
        z.object({
          kind: z.literal('password'),
          hasStoredPassword: z.boolean().default(false),
        }),
      ]),
      sshConfigAlias: z.string().optional(),
    })
    .superRefine((val, ctx) => {
      if (!val.enabled) return;
      if (val.host.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['host'], message: 'SSH host is required' });
      } else if (/[\s,\[\]\x00]/.test(val.host)) {
        // Defense in depth — reject known_hosts-style grammar separators. The
        // host gets written into ~/.ssh/jam_known_hosts by sshKnownHosts.ts
        // which has the same guard; catching it here gives the user a
        // form-level error before they hit the connect path. IPv6 hosts are
        // entered BARE here (e.g. `2001:db8::1`); the `[...]:port` wrapping
        // happens internally.
        ctx.addIssue({
          code: 'custom',
          path: ['host'],
          message: 'SSH host must not contain whitespace, commas, or brackets',
        });
      }
      if (val.user.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['user'], message: 'SSH user is required' });
      }
      if (val.auth.kind === 'key' && val.auth.keyPath.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['auth', 'keyPath'], message: 'Key file is required' });
      }
    })
    .optional(),
  // MCP permission level override
  mcpPermissionLevel: z
    .union([
      z.literal('inherit'),
      z.literal('block'),
      z.literal('read-only'),
      z.literal('confirm'),
    ])
    .optional()
    .default('inherit'),
  // Connection color accent
  color: z.string().optional(),
});

export type ConnectionFormValues = z.infer<typeof connectionFormSchema>;

/**
 * Kusto Service Principal auth requires a tenant ID — `ClientSecretCredential`
 * throws at token-acquisition time when left at the 'common' default (see
 * entra-auth NOTES). This is a field-level validator rather than a
 * `connectionFormSchema.superRefine()` because wrapping the whole schema in
 * `.superRefine()` turns it into a `ZodEffects`, which has no `.shape` — and
 * `ConnectionFormFields.tsx` relies on `connectionFormSchema.shape.server` /
 * `.shape.port` / `.shape.authentication` for its per-field TanStack Form
 * validators.
 */
export function validateKustoTenantId(
  authentication: AuthenticationType,
  tenantId: string | undefined
): string | undefined {
  if (authentication === 'Service Principal' && !tenantId?.trim()) {
    return 'Tenant ID is required for Service Principal authentication';
  }
  return undefined;
}

/**
 * Detect if running on Windows.
 * On Windows, we default to '<auto>' port which triggers SQL Tools Service
 * (better support for local protocols like Shared Memory/Named Pipes).
 * On other platforms, we default to 1433 (standard TCP port).
 */
const isWindows = typeof navigator !== 'undefined' && navigator.platform?.toLowerCase().includes('win');

export const connectionDefaultValues: ConnectionFormValues = {
  engine: 'mssql',
  alias: '',
  server: '',
  port: isWindows ? '<auto>' : '1433',
  authentication: 'SQL Server authentication',
  login: '',
  password: '',
  database: '',
  limitToDatabase: false,
  entraTenantId: '',
  encrypt: true,
  trustServerCertificate: true,
  ssl: false,
  readOnly: false,
  mcpPermissionLevel: 'inherit',
  color: undefined,
  sshTunnel: {
    enabled: false,
    host: '',
    port: 22,
    user: '',
    auth: { kind: 'agent' },
  },
};
